import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  GridFormPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridFormPopupSizeKey
} from '../opsDataGrid';
import { showDeleteConfirmDialog } from '../dialogs/confirmDialog';

export interface ClientUserActivityOmitEmailRow {
  id: number;
  email: string;
  description?: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface ClientUserActivityOmitEmailUpsertBody {
  email: string;
  description?: string | null;
  isActive: boolean;
}

export interface ClientUserActivityOmitEmailsAdminApi {
  list: () => Promise<ClientUserActivityOmitEmailRow[]>;
  create: (body: ClientUserActivityOmitEmailUpsertBody) => Promise<ClientUserActivityOmitEmailRow>;
  update: (id: number, body: ClientUserActivityOmitEmailUpsertBody) => Promise<ClientUserActivityOmitEmailRow>;
  delete: (id: number) => Promise<void>;
}

export interface ClientUserActivityOmitEmailsAdminGridProps {
  api: ClientUserActivityOmitEmailsAdminApi;
  stateStorageKey: string;
}

const GRID_SCHEMA_VERSION = 1;
const GRID_COLUMNS = ['email', 'description', 'isActive', 'modifiedAt', 'modifiedBy'] as const;

const emptyForm = (): ClientUserActivityOmitEmailUpsertBody => ({
  email: '',
  description: '',
  isActive: true
});

const fieldLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--ops-color-text, #111827)'
};

export function ClientUserActivityOmitEmailsAdminGrid({
  api,
  stateStorageKey
}: ClientUserActivityOmitEmailsAdminGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [rows, setRows] = useState<ClientUserActivityOmitEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ClientUserActivityOmitEmailUpsertBody>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list());
      setFormError(null);
    } catch (e) {
      setRows([]);
      setFormError(e instanceof Error ? e.message : 'Failed to load tester omit accounts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (row: ClientUserActivityOmitEmailRow) => {
    setEditingId(row.id);
    setForm({
      email: row.email,
      description: row.description ?? '',
      isActive: row.isActive
    });
    setFormError(null);
    setEditorOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body: ClientUserActivityOmitEmailUpsertBody = {
        email: form.email.trim(),
        description: form.description?.trim() || null,
        isActive: form.isActive
      };
      if (editingId == null) {
        await api.create(body);
      } else {
        await api.update(editingId, body);
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (editingId == null) return;
    if (!(await showDeleteConfirmDialog('Delete this tester omit account?'))) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.delete(editingId);
      setEditorOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && rows.length === 0 ? (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
          <LoadIndicator visible height={20} width={20} />
          <span>Loading…</span>
        </div>
      ) : (
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          noDataText="No tester omit accounts — click Add account to hide a tester by email or name."
          onRowDblClick={(e) => onGridRowDblClick(e, openEdit)}
          onKeyDown={(e) => onGridFocusedRowEnter(e, openEdit)}
        >
          <OpsGridStateStoring
            storageKey={stateStorageKey}
            schemaVersion={GRID_SCHEMA_VERSION}
            columns={GRID_COLUMNS}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh tester omit accounts"
              newItem={{ hint: 'Add account', onClick: openCreate }}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <SearchPanel visible width={240} placeholder="Search email or name…" />
          <HeaderFilter visible />
          <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
          <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
          <Paging defaultPageSize={25} />
          <Column dataField="email" caption="Email or name" minWidth={180} />
          <Column dataField="description" caption="Description" minWidth={160} />
          <Column dataField="isActive" caption="Active" dataType="boolean" width={90} />
          <Column dataField="modifiedAt" caption="Modified" dataType="datetime" width={170} />
          <Column dataField="modifiedBy" caption="Modified by" minWidth={120} visible={false} />
        </DataGrid>
      )}

      <GridFormPopup
        visible={editorOpen}
        title={editingId == null ? 'Add tester omit account' : 'Edit tester omit account'}
        onClose={() => setEditorOpen(false)}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        error={formError}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <div>
              {editingId != null ? (
                <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button text="Cancel" disabled={saving} onClick={() => setEditorOpen(false)} />
              <Button
                text={saving ? 'Saving…' : 'Save'}
                type="default"
                disabled={saving}
                onClick={() => void save()}
              />
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ops-color-muted, #6b7280)' }}>
            Active entries are hidden from every Client Access Tracking report and grid. Use the
            sign-in email, UPN, or the name shown in User Activity (for example Pauluul Wisniewski).
            Session history is kept so removing an entry restores that user immediately.
          </p>
          <label>
            <span style={fieldLabel}>Email or name</span>
            <TextBox value={form.email} onValueChanged={(e) => setForm((f) => ({ ...f, email: e.value ?? '' }))} />
          </label>
          <label>
            <span style={fieldLabel}>Description (optional)</span>
            <TextBox
              value={form.description ?? ''}
              onValueChanged={(e) => setForm((f) => ({ ...f, description: e.value ?? '' }))}
            />
          </label>
          <CheckBox
            text="Active"
            value={form.isActive}
            onValueChanged={(e) => setForm((f) => ({ ...f, isActive: !!e.value }))}
          />
        </div>
      </GridFormPopup>
    </>
  );
}
